"use client";
import { submitContactUsForm } from "@/server/contact-actions";
import { useState } from "react";
import dynamic from 'next/dynamic';
import Breadcrumbs from "@/components/common/Breadcrumbs";

const ScrollToTopButton = dynamic(() => import('@/components/ui/ScrollToTopButton'), {
  ssr: false
});

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const ContactUsComponent = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isFormValid = 
    formData.name.trim() !== "" && 
    validateEmail(formData.email) && 
    formData.message.trim() !== "";

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    setIsLoading(true);

    try {
      const result = await submitContactUsForm(formData);

      if (result.success) {
        setResponseMessage("Woohoo! Your message is on its way. Thank you!");
        setFormData({ name: "", email: "", message: "" });
        setIsSubmitted(true);
        setIsError(false);
        setTimeout(() => setIsSubmitted(false), 10000); // Reset confirmation message after 10 seconds
      } else {
        setErrorMessage("Oops! Something went wrong. Please try again.");
        setIsError(true);
        setIsSubmitted(false);
        setTimeout(() => setIsError(false), 10000); // Reset confirmation message after 10 seconds
      }
    } catch (error) {
      setErrorMessage("Network error. Please try again later.");
      setIsError(true);
      setIsSubmitted(false);
      setTimeout(() => setIsError(false), 10000); // Reset confirmation message after 10 seconds
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white pb-16 pt-4">
        <Breadcrumbs
          crumbs={[
            { label: "Contact Us", href: "/contact-us" }
          ]} 
          hasBanner={true}
          className="pb-12"
        />

        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Get in Touch
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 mb-6">
            Have questions, suggestions, or feedback? We&lsquo;d love to hear from you!
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Send us a Message
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  We&lsquo;ll get back to you as soon as possible
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ring-offset-transparent dark:bg-gray-700 dark:text-white transition duration-200"
                    name="name"
                    placeholder="What's your name? Let us know!"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ring-offset-transparent dark:bg-gray-700 dark:text-white transition duration-200 ${
                      formData.email && !validateEmail(formData.email)
                        ? "border-red-500 dark:border-red-500 focus:ring-red-500"
                        : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                    }`}
                    type="email"
                    name="email"
                    placeholder="Your email, so we can reach out!"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  {formData.email && !validateEmail(formData.email) && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400 text-left">
                      Please enter a valid email address.
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ring-offset-transparent dark:bg-gray-700 dark:text-white transition duration-200 resize-none"
                    name="message"
                    placeholder="What's on your mind? We're all ears!"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={isLoading || !isFormValid}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:enabled:from-blue-700 hover:enabled:to-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:enabled:scale-105 shadow-md hover:enabled:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Message
                    </span>
                  )}
                </button>
              </form>

              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
                Fields marked with <span className="text-red-500">*</span> are required.
              </p>

              {/* Success Message */}
              {isSubmitted && (
                <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      {responseMessage || "Woohoo! Your message is on its way. Thank you!"}
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {isError && (
                <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {errorMessage || "Oops! Something went wrong. Please try again."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <ScrollToTopButton 
        gradientColors="from-blue-600 to-purple-600" 
        hoverColors="hover:from-blue-700 hover:to-purple-700" 
      />
    </div>
  );
};

export default ContactUsComponent;
